(ns ui.sticker-dialogs.hashtag
  "The Replicant hashtag sticker dialog: type or paste hashtags, pick
   recent ones, choose a design (or the invisible one) and add up to
   five hashtag stickers to the story. With its
   `:sticker-dialog/hashtag-*` action handlers."
  (:require [datascript.core :as ds]
            [storrito.editor.sticker-design :as sticker-design]
            [ui.sticker-dialogs.actions :as actions]
            [ui.sticker-dialogs.emoji-picker :as emoji-picker]
            [ui.sticker-dialogs.model :as model]
            [ui.sticker-dialogs.recent :as recent]
            [ui.sticker-dialogs.storage :as storage]
            [ui.sticker-dialogs.ui :as ui]))

(def input-id
  "sticker-hashtag-input")

(def invisible-design
  {:text-props {:fontFamily ui/instagram-font
                :fill "#ffffff"
                :fontSize 120
                :top 5
                :left 40}
   :bg-color "#cccccc"
   :rx 20
   :ry 20})

(defn count-in-story
  "How many hashtag stickers the story already has."
  [db]
  (if db
    (count (ds/q '[:find [?clip ...]
                   :where
                   [?asset :asset/type :hashtag-sticker]
                   [?clip :clip/asset ?asset]]
                 db))
    0))

(defn initial-state
  [{:keys [db]}]
  {:hashtags []
   :input ""
   :design sticker-design/default-hashtag-design
   :recent (storage/recent-hashtags)
   :current-count (count-in-story db)})

(defn add-actions
  "The actions that add one hashtag sticker per hashtag."
  [{:keys [design invisible?]} hashtags]
  (mapcat (fn [hashtag]
            (actions/asset-actions
              nil
              (actions/asset-tx nil
                                :hashtag-sticker
                                (str "#" hashtag)
                                (actions/invisible
                                  (assoc actions/text-sticker-base
                                         :text (str "#" hashtag)
                                         :type "hashtag"
                                         :design (or design
                                                     sticker-design/default-hashtag-design))
                                  invisible?))))
          hashtags))

(defn submit!
  [{:keys [store ui/event-handler]}]
  (let [dialog (actions/dialog @store)
        hashtags (model/hashtags-for-submit dialog)]
    (when (and (seq hashtags)
               (model/within-hashtag-limit? dialog []))
      (storage/remember-hashtags! hashtags)
      (event-handler {}
                     (concat (add-actions dialog hashtags)
                             [[:ui/modal-close]])))))

(defn commit!
  "Enter or the Add button: the typed hashtags become chips and are
   remembered as recent ones."
  [{:keys [store]}]
  (let [before (actions/dialog @store)
        after (model/commit-input before)
        added (remove (set (:hashtags before))
                      (:hashtags after))]
    (when (seq added)
      (storage/remember-hashtags! added))
    (actions/swap-dialog! store (fn [_]
                                  (cond-> after
                                    (seq added) (assoc :recent (storage/recent-hashtags)))))))

(defn paste!
  "A paste of comma separated hashtags adds them right away."
  [{:keys [store] :as w}]
  (let [event (:replicant/js-event w)
        text (some-> (.-clipboardData event)
                     (.getData "text"))
        hashtags (model/extract-hashtags text)]
    (when (seq hashtags)
      (.preventDefault event)
      (let [dialog (actions/dialog @store)]
        (if (model/within-hashtag-limit? dialog hashtags)
          (do
            (storage/remember-hashtags! hashtags)
            (actions/swap-dialog! store (fn [dialog]
                                          (-> dialog
                                              (model/add-hashtags hashtags)
                                              (assoc :recent (storage/recent-hashtags))))))
          (actions/swap-dialog! store assoc :error :hashtag-limit))))))

(defn toggle!
  [{:keys [store action]}]
  (let [[_ hashtag] action
        dialog (actions/dialog @store)
        selected? (contains? (set (:hashtags dialog))
                             (model/normalize-hashtag hashtag))]
    (when-not selected?
      (storage/remember-hashtags! [hashtag]))
    (actions/swap-dialog! store model/toggle-hashtag hashtag)))

(defn forget!
  [{:keys [store action]}]
  (let [[_ entry] action]
    (storage/forget-hashtag! entry)
    (actions/swap-dialog! store (fn [dialog]
                                  (-> dialog
                                      (model/remove-hashtag (:hashtag entry))
                                      (assoc :recent (storage/recent-hashtags)))))))

(defn token-input
  [dialog]
  (let [pending (model/pending-hashtags dialog)
        within-limit? (model/within-hashtag-limit? dialog pending)]
    [:div.flex.items-center.relative
     (ui/text-field dialog
                    {:field :input
                     :dom-id input-id
                     :placeholder "Type hashtag and press Enter"
                     :class ["uppercase" "text-xl" "border" "border-gray-300" "rounded" "p-2" "w-full"]
                     :style {:font-family ui/instagram-font}
                     :autofocus? true})
     (ui/emoji-button {:field :input
                       :dom-id input-id})
     (emoji-picker/panel dialog :input)
     [:button {:type "button"
               :class (concat ["ml-2"] ui/secondary-css)
               :disabled (or (empty? pending)
                             (not within-limit?))
               :on {:click [[:js/prevent-default]
                            [:sticker-dialog/hashtag-commit]]}}
      "Add"]]))

(defn recent-chips
  [{:keys [recent hashtags manage-recent?] :as dialog}]
  (when (seq recent)
    (let [selected (set hashtags)]
      [:div.mt-3
       [:div.flex.items-center.justify-between.mb-2
        [:div.text-xs.font-medium.text-gray-500 "Recent hashtags"]
        [:button.text-xs.text-gray-500.hover:text-gray-800
         {:type "button"
          :on {:click [[:js/prevent-default]
                       [:sticker-dialog/assoc :manage-recent? (not manage-recent?)]]}}
         (if manage-recent? "Done" "Manage recent")]]
       [:div.flex.flex-wrap.gap-2
        (map (fn [{:keys [hashtag] :as entry}]
               (let [selected? (contains? selected (model/normalize-hashtag hashtag))
                     limit-reached? (and (not selected?)
                                         (not (model/within-hashtag-limit? dialog [hashtag])))]
                 [:span {:replicant/key (recent/hashtag-key entry)}
                  (ui/chip {:label (str "#" hashtag)
                            :selected? selected?
                            :disabled? (or manage-recent? limit-reached?)
                            :title (cond
                                     manage-recent? (str "Use × to remove #" hashtag " from recent hashtags")
                                     selected? (str "Remove #" hashtag " from selection")
                                     limit-reached? "Hashtag limit reached"
                                     :else (str "Add #" hashtag))
                            :actions (when-not (or manage-recent? limit-reached?)
                                       [[:sticker-dialog/hashtag-toggle hashtag]])
                            :remove-actions (when manage-recent?
                                              [[:sticker-dialog/hashtag-forget entry]])
                            :remove-title (str "Remove #" hashtag " from recent hashtags")})]))
             recent)]
       [:p.mt-2.text-xs.text-gray-500
        (if manage-recent?
          "Use × to remove hashtags from your recent list."
          "Click recent hashtags to select multiple.")]])))

(defn design-list
  [{:keys [design invisible?]} designs]
  [:div.flex.flex-wrap.gap-3
   (map-indexed (fn [idx entry]
                  [:span {:replicant/key idx}
                   (ui/design-entry {:text "#HASHTAG"
                                     :design entry
                                     :active? (and (not invisible?)
                                                   (= entry design))
                                     :actions [[:sticker-dialog/assoc :design entry :invisible? false]]})])
                designs)])

(defn panel
  [dialog]
  (let [preview (model/hashtags-for-submit dialog)
        total (model/hashtag-total dialog [])
        over-limit? (> total model/hashtag-limit)]
    [:form.my-5.mx-auto.max-w-xl.flex.flex-col
     {:on {:submit [[:js/prevent-default]
                    [:sticker-dialog/hashtag-submit]]
           :keydown [[:js/on-enter]
                     [:js/prevent-default]
                     [:sticker-dialog/hashtag-commit]]
           :paste [[:sticker-dialog/hashtag-paste]]}}
     (ui/title "#HASHTAG" (ui/gradient-text-style "#f58c5c" "#ff4f99"))
     [:label.font-bold.mb-1
      (str "Enter hashtag(s) " total "/" model/hashtag-limit)]
     (token-input dialog)
     (recent-chips dialog)
     [:small [:i "Press Enter or click Add to save typed hashtags. Active recent hashtags will be added to the design."]]
     (when (or over-limit?
               (= :hashtag-limit (:error dialog)))
       [:span.text-red-600
        (str "You can only add " model/hashtag-limit " hashtags.")])
     [:label.font-bold.mt-4 "New designs"]
     (design-list dialog sticker-design/new-hashtag-designs)
     [:label.font-bold.mt-4 "Legacy designs"]
     (design-list dialog (vals sticker-design/designs))
     [:div.mt-4
      [:label.font-bold "Invisible"]
      [:span.text-gray-500.text-sm " (not visible, but functional on Instagram)"]
      [:div.flex.flex-wrap.gap-3
       (ui/design-entry {:text "#INVISIBLE"
                         :design invisible-design
                         :active? (:invisible? dialog)
                         :actions [[:sticker-dialog/assoc :design invisible-design :invisible? true]]})]]
     [:div.mt-4.flex.justify-center
      (ui/primary-button {:type "submit"
                          :disabled (or (empty? preview)
                                        over-limit?)}
                         (if (< 1 (count preview))
                           (str "Add " (count preview) " Hashtags")
                           "Add Hashtag"))]]))

(defn render
  [state]
  (when-let [dialog (actions/dialog state)]
    (panel dialog)))

(def modal
  {:modal-id :modals/editor2-hashtag-modal
   :on-load (fn [_state] [[:sticker-dialog/init :hashtag]])
   :sticker/kind :hashtag
   :sticker/initial-state #'initial-state
   :render #'render})

(def register
  [modal
   (actions/handler :sticker-dialog/hashtag-commit commit!)
   (actions/handler :sticker-dialog/hashtag-paste paste!)
   (actions/handler :sticker-dialog/hashtag-toggle toggle!)
   (actions/handler :sticker-dialog/hashtag-forget forget!)
   (actions/handler :sticker-dialog/hashtag-submit submit!)])
